/// Per-character attempt history. Each game writes one row per shown
/// card / round; the store keeps a bounded ring buffer per (module,
/// character) tuple so we can compute a rolling hit-rate without
/// unbounded growth. 256 attempts × ~60 characters × ~6 modules
/// stays well under the 5 MB localStorage budget on every browser.
///
/// Persistence format is versioned so future migrations don't
/// silently corrupt history. Bump `SCHEMA_VERSION` and add a branch
/// in `loadHistory()` if the shape changes.

export interface Attempt {
  /// Module the attempt came from, e.g. "morse" / "semaphore".
  module: string;
  /// Single Unicode character or short symbol identifier (e.g. "A",
  /// "7"). The game decides — `games-core` doesn't validate.
  char: string;
  /// True if the player answered correctly on the first try.
  ok: boolean;
  /// Milliseconds from card-shown to first answer. Useful for
  /// "beat the clock" scoring.
  ms: number;
  /// Wall-clock timestamp at attempt time.
  ts: number;
}

export interface CharStats {
  /// Total attempts for this character (across all games in the module).
  attempts: number;
  /// Number of correct first-tries.
  correct: number;
  /// Mean response time in ms across all attempts.
  meanMs: number;
  /// Hit-rate 0–1 inclusive. 0 when `attempts === 0`.
  hitRate: number;
}

const SCHEMA_VERSION = 1;
const KEY_PREFIX = "nav-games.attempts.v" + SCHEMA_VERSION + ".";
const RING_SIZE = 256;

interface Stored {
  /// FIFO ring of recent attempts. New entries push, oldest drops
  /// off when length exceeds RING_SIZE.
  ring: Attempt[];
}

export function recordAttempt(
  attempt: Attempt,
  storage: Storage = defaultStorage(),
): void {
  const key = keyFor(attempt.module, attempt.char);
  const cur = readKey(key, storage);
  cur.ring.push(attempt);
  if (cur.ring.length > RING_SIZE) {
    cur.ring.splice(0, cur.ring.length - RING_SIZE);
  }
  try {
    storage.setItem(key, JSON.stringify(cur));
  } catch {
    /* quota exceeded or storage unavailable — drop the write */
  }
}

export function statsFor(
  module: string,
  char: string,
  storage: Storage = defaultStorage(),
): CharStats {
  const { ring } = readKey(keyFor(module, char), storage);
  if (ring.length === 0) {
    return { attempts: 0, correct: 0, meanMs: 0, hitRate: 0 };
  }
  let correct = 0;
  let totalMs = 0;
  for (const a of ring) {
    if (a.ok) correct += 1;
    totalMs += a.ms;
  }
  return {
    attempts: ring.length,
    correct,
    meanMs: totalMs / ring.length,
    hitRate: correct / ring.length,
  };
}

/// Spaced-repetition style picker — choose the next card from
/// `pool`, biased toward cards the player has done worst on
/// recently. Cards in `recent` are excluded so the same card
/// doesn't appear twice in a short window. Cards with no history
/// are picked with a moderate weight so the deck still gets
/// fully exercised early on.
///
/// Weighting model:
///   - unseen (attempts === 0) → weight 1.0 (introduce regularly)
///   - 100 % hit rate, ≥ 5 attempts → weight 0.2 (de-emphasised
///     because the player has mastered them)
///   - everything else → weight 0.4 / (hitRate + 0.25), so a card
///     at 50 % gets ~0.53 and a card at 0 % gets ~1.6 — three to
///     four times more likely than a mastered card.
///
/// The returned key is uniformly random within the weighted
/// distribution. Falls back to plain random when `pool` is
/// entirely in `recent` (which can happen with a small pool).
export function pickWeightedKey(
  module: string,
  pool: readonly string[],
  recent: readonly string[] = [],
  options: { rng?: () => number; storage?: Storage } = {},
): string {
  const rng = options.rng ?? Math.random;
  const storage = options.storage;
  // First pass — weight every key, excluding recents.
  const weights: number[] = [];
  let total = 0;
  for (const key of pool) {
    if (recent.includes(key)) {
      weights.push(0);
      continue;
    }
    const stats = storage ? statsFor(module, key, storage) : statsFor(module, key);
    const w = weightFor(stats);
    weights.push(w);
    total += w;
  }
  // If everything is recent, drop the recent filter and try again.
  if (total === 0) {
    for (let i = 0; i < pool.length; i++) {
      const stats = storage
        ? statsFor(module, pool[i]!, storage)
        : statsFor(module, pool[i]!);
      const w = weightFor(stats);
      weights[i] = w;
      total += w;
    }
  }
  if (total === 0) {
    // Pool is empty — caller's problem, but pick first as a fallback.
    return pool[0]!;
  }
  let target = rng() * total;
  for (let i = 0; i < pool.length; i++) {
    const w = weights[i]!;
    if (w === 0) continue;
    target -= w;
    if (target <= 0) return pool[i]!;
  }
  return pool[pool.length - 1]!;
}

function weightFor(stats: CharStats): number {
  if (stats.attempts === 0) return 1.0;
  if (stats.attempts >= 5 && stats.hitRate >= 1.0) return 0.2;
  return 0.4 / (stats.hitRate + 0.25);
}

/// Clear all attempt history for a single module. Used when a player
/// wants a fresh start, or on sign-out (decided by the caller —
/// `games-core` doesn't reach into auth).
export function clearModule(
  module: string,
  storage: Storage = defaultStorage(),
): void {
  const prefix = KEY_PREFIX + module + ".";
  const toRemove: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k && k.startsWith(prefix)) toRemove.push(k);
  }
  for (const k of toRemove) storage.removeItem(k);
}

function keyFor(module: string, char: string): string {
  return KEY_PREFIX + module + "." + char;
}

function readKey(key: string, storage: Storage): Stored {
  try {
    const raw = storage.getItem(key);
    if (!raw) return { ring: [] };
    const parsed = JSON.parse(raw) as Partial<Stored>;
    if (!Array.isArray(parsed.ring)) return { ring: [] };
    return { ring: parsed.ring };
  } catch {
    return { ring: [] };
  }
}

function defaultStorage(): Storage {
  return typeof window !== "undefined" ? window.localStorage : memoryStorage();
}

function memoryStorage(): Storage {
  const map = new Map<string, string>();
  return {
    get length() {
      return map.size;
    },
    clear: () => map.clear(),
    getItem: (k) => map.get(k) ?? null,
    key: (i) => [...map.keys()][i] ?? null,
    removeItem: (k) => {
      map.delete(k);
    },
    setItem: (k, v) => {
      map.set(k, v);
    },
  };
}
